import * as THREE from 'three';
import { DRAGON } from '@somnhold/content';
import { laneLit, type BattleRules, type BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { posePoint, type AnimParams } from '../anim/atlas.ts';
import { GlowLayer } from './glow.ts';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * The dragon of the Burn's clearing (docs/UNICAS.md §4, §6): the old harpoons on its rim with their lanes, the chains
 * of the ones stuck in it, its six glass scales on the model, and its fury's glass coming in from the rim.
 *
 * - The harpoons: the first Vigil's engines (the old trap's, without its winches), aimed at the middle, each lever on
 *   its post beside it. Their lane on the ground lies faint, and lights dragon red as it comes round to cross it; the
 *   lever glows fuller as a hero holds it. Fired, the harpoon flies into the dragon and its chain hangs from it.
 * - The scales: six plates of black glass on its flanks, back, haunches and chest. They frost over as the cold fills,
 *   flicker white when brittle, burst in glass when one breaks (a red-hot wound left where it was), and grow back in
 *   orange fire when fire closes one. The ones that never close (the diary's crack, the scars) keep a pale split.
 * - The fury: the rim's ground turns to burning glass ring by ring, the next ring pulsing red before it does.
 *
 * Draw calls: the engines (one merged mesh), the harpoons, the levers, the scales and the chains (instanced, one
 * each), the lanes (one mesh), the glass (one plane, only while it rages), a glow layer. No lights; everything is
 * built when the board opens so its shaders compile with the rest.
 */

type Field = NonNullable<BattleRules['dragon']>;

const BEAM = ['#1f1a1a', '#262021', '#1a1617'];
const IRON = '#4a4644';
const IRON_DARK = '#2e2b2b';
const ROPE = '#6b5a4a';
const EMBER = new THREE.Color('#ff4f3a');
const SOFT = new THREE.Color('#ff8a6e');
const FIRE = new THREE.Color('#ff8a3d');
const ICE = new THREE.Color('#cfe2ff');
const WHOLE = new THREE.Color('#4a6a98');
const FROST = new THREE.Color('#c8e4ff');
const WHITE = new THREE.Color('#f2f6ff');
const SHARDS = '#cfe2ff';

const Y_AXIS = new THREE.Vector3(0, 1, 0);
/** The six corners of a hex ring, as axial steps (q, r), in order round it. */
const HEX_DIRS: [number, number][] = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
const Z_AXIS = new THREE.Vector3(0, 0, 1);

/** The harpoon's flight into it (s). */
const FLIGHT = 0.22;
/** Links in each chain. */
const LINKS = 12;

/**
 * Where the six plates sit on its body (model space, landed; in flight a little lower), facing out: left and right
 * flank under the wings, left and right haunch, the back ridge, the chest. The first is the diary's (under the left
 * wing, where the first Vigil's harpoon cracked it).
 */
const PLATES: [number, number, number, number][] = [
  [-0.8, 1.95, 0.7, -Math.PI / 2],
  [0.8, 1.95, 0.7, Math.PI / 2],
  [-0.72, 1.6, -0.75, -Math.PI / 2],
  [0.72, 1.6, -0.75, Math.PI / 2],
  [0, 2.55, 0.15, 0],
  [0, 1.45, 1.45, 0],
];

const laneMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uOn: { value: new THREE.Vector4() }, uT: { value: 0 } },
    vertexShader: /* glsl */ `
      attribute float aLane;
      varying vec2 vUv;
      varying float vLane;
      void main() {
        vUv = uv;
        vLane = aLane;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec4 uOn;
      uniform float uT;
      varying vec2 vUv;
      varying float vLane;
      void main() {
        float on = vLane < 0.5 ? uOn.x : vLane < 1.5 ? uOn.y : vLane < 2.5 ? uOn.z : uOn.w;
        // Across: two bold edges and a faint floor; along: chevrons running in from the engine, fast when lit.
        float x = abs(vUv.x - 0.5) * 2.0;
        float edge = smoothstep(0.6, 0.8, x) * (1.0 - smoothstep(0.92, 1.0, x));
        float chev = fract(vUv.y * 6.0 - abs(vUv.x - 0.5) * 0.9 - uT * mix(0.1, 1.4, on));
        float mark = smoothstep(0.0, 0.06, chev) * (1.0 - smoothstep(0.22, 0.32, chev)) * (1.0 - smoothstep(0.6, 0.75, x));
        float ends = smoothstep(0.0, 0.06, vUv.y) * (1.0 - smoothstep(0.85, 1.0, vUv.y));
        vec3 slack = vec3(0.62, 0.5, 0.48);
        vec3 live = vec3(1.0, 0.31, 0.23);
        vec3 col = mix(slack, live, on);
        float a = (edge * mix(0.16, 0.95, on) + mark * mix(0.05, 0.65, on) + mix(0.02, 0.14, on)) * ends;
        a *= 0.85 + 0.15 * sin(uT * 7.0) * on;
        gl_FragColor = vec4(col, a);
      }`,
  });

/**
 * The fury's glass (sim hexes, the sim's own rounding): past ring `uGlass` the ground is the Burn's black glass, faceted
 * and glossy, its facets' seams split with embers that beat dragon red; its front, where it is coming in, burns
 * white-hot and shimmers; ring `uGlass` itself is warned, red and pulsing, stripes running inward, before it goes.
 */
const glassMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uGlass: { value: 99 }, uWarn: { value: 0 }, uT: { value: 0 }, uK: { value: WORLD_PER_HEX / 1000 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() {
        vec4 w = modelMatrix * vec4(position, 1.0);
        vW = w.xyz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uGlass;
      uniform float uWarn;
      uniform float uT;
      uniform float uK;
      varying vec3 vW;
      float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      void main() {
        vec2 p = vW.xz / uK;
        // Axial (fractional) and its rounded hex, as the sim rounds: the ring is exact, the front continuous.
        float r = p.y / 866.0;
        float q = p.x / 1000.0 - r * 0.5;
        float hf = (abs(q) + abs(r) + abs(q + r)) * 0.5;
        float s = -q - r;
        float rq = floor(q + 0.5), rr = floor(r + 0.5), rs = floor(s + 0.5);
        float dq = abs(rq - q), dr = abs(rr - r), ds = abs(rs - s);
        if (dq > dr && dq > ds) rq = -rr - rs;
        else if (dr > ds) rr = -rq - rs;
        float d = (abs(rq) + abs(rr) + abs(rq + rr)) * 0.5;
        vec3 col = vec3(0.0);
        float a = 0.0;
        if (d > uGlass + 0.5) {
          // Facets: cells of glass, each its own dark tone and its own glint as a slow sheen crosses the clearing.
          vec2 c = p / 560.0;
          vec2 i = floor(c);
          vec2 f = fract(c);
          float d1 = 8.0, d2 = 8.0;
          vec2 cell = i;
          for (int y = -1; y <= 1; y++)
            for (int x = -1; x <= 1; x++) {
              vec2 o = vec2(float(x), float(y));
              vec2 pt = o + vec2(h21(i + o), h21(i + o + 7.3)) * 0.85 + 0.075;
              float l = length(f - pt);
              if (l < d1) { d2 = d1; d1 = l; cell = i + o; } else if (l < d2) d2 = l;
            }
          float h = h21(cell);
          // Covered glass: black and specular, a cold sheen sliding over its facets; the seams cold too, warming to
          // embers only toward the front that is still coming in. A slow dark-red beat says all of it burns.
          vec3 glass = mix(vec3(0.002, 0.002, 0.004), vec3(0.012, 0.013, 0.02), h * h);
          float sheen = pow(max(0.0, sin(dot(p, vec2(0.0006, 0.0011)) - uT * 0.8 + h * 3.0)), 70.0);
          glass += vec3(0.16, 0.2, 0.3) * sheen * h;
          float seam = 1.0 - smoothstep(0.01, 0.05, d2 - d1);
          float beat = 0.5 + 0.5 * sin(uT * 3.1 + h * 6.28 + d * 0.9);
          float near = 1.0 - smoothstep(0.0, 2.5, hf - (uGlass + 0.5));
          vec3 ember = mix(vec3(0.55, 0.05, 0.03), vec3(1.0, 0.31, 0.23), beat);
          vec3 cold = vec3(0.07, 0.09, 0.14) * (0.6 + 0.4 * h);
          col = glass + mix(cold, ember * (0.6 + 0.9 * beat), near) * seam;
          col += vec3(0.022, 0.0, 0.002) * (0.5 + 0.5 * sin(uT * 1.6 + d * 0.5));
          // The front: white-hot where it comes in, shimmering with the heat.
          float front = 1.0 - smoothstep(0.0, 1.2, hf - (uGlass + 0.5));
          float shimmer = 0.75 + 0.25 * sin(uT * 11.0 + p.x * 0.013 + p.y * 0.017) * sin(uT * 7.0 - p.y * 0.011);
          col += vec3(1.0, 0.42, 0.16) * front * front * 1.5 * shimmer;
          col += vec3(1.0, 0.85, 0.6) * pow(front, 6.0) * 1.2 * shimmer;
          vec2 sc = floor(p / 70.0);
          float sp = h21(sc + floor(uT * 6.0));
          col += vec3(1.0, 0.7, 0.4) * step(0.985, sp) * front * 2.0;
          a = 0.97;
        } else if (d > uGlass - 0.5 && uWarn > 0.0) {
          // The next ring: red, pulsing faster as it comes, stripes running in toward the middle.
          float beat = 0.5 + 0.5 * sin(uT * mix(5.0, 16.0, uWarn));
          float stripe = step(0.5, fract(hf * 3.0 + uT * 1.6));
          col = vec3(1.0, 0.31, 0.23) * (0.55 + 0.6 * beat) * mix(0.6, 1.0, stripe);
          a = (0.3 + 0.45 * beat) * uWarn * mix(0.65, 1.0, stripe);
        }
        gl_FragColor = vec4(col, a);
      }`,
  });

export class DragonFieldLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private n: number;
  private guns: { at: THREE.Vector3; mouth: THREE.Vector3; lever: THREE.Vector3; yaw: number; rest: THREE.Matrix4 }[] = [];
  private harpoons: THREE.InstancedMesh;
  private levers: THREE.InstancedMesh;
  private chains: THREE.InstancedMesh;
  private plates: THREE.InstancedMesh;
  private lanes: THREE.ShaderMaterial;
  private glass: THREE.ShaderMaterial;
  private glassMesh: THREE.Mesh;
  private now = 0;
  /** Each harpoon: fired at (s; -1 none), stuck in it now, loaded, the lever's hold (0..1), lit (eased 0..1). */
  private fired: number[];
  private stuck: boolean[];
  /** Brought down: the chains that held it, drawn taut a moment as it falls (s), and when it hits the ground. */
  private taut: number[];
  private impact: { at: number; p: THREE.Vector3 } | null = null;
  private loaded: boolean[];
  private hold: number[];
  private lit: number[];
  private pulled: number[];
  /** The dragon this frame (set by the view as it draws it): where, its turn, its size, in flight, its pose. */
  private body: { p: THREE.Vector3; q: THREE.Quaternion; scale: number; flying: boolean; anim: AnimParams | undefined } | null = null;
  private bodyBuf: { p: THREE.Vector3; q: THREE.Quaternion; scale: number; flying: boolean; anim: AnimParams | undefined } = { p: new THREE.Vector3(), q: new THREE.Quaternion(), scale: 1, flying: false, anim: undefined };
  /** Each plate: shown (0 gone .. 1 whole, eased), its state now, when it last broke or closed (s). */
  private shown = [1, 1, 1, 1, 1, 1];
  private broken = 0;
  private fixed: number;
  private cold = 0;
  private brittle = false;
  private closed: number[] = [-9, -9, -9, -9, -9, -9];
  private fury = false;
  /** The fury's safe ground (hexes; -1: no fury), where its front's sparks fly. */
  private glassR = -1;
  private m = new THREE.Matrix4();
  private hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  private v = new THREE.Vector3();
  private a = new THREE.Vector3();
  private b = new THREE.Vector3();
  private to = new THREE.Vector3();
  private one = new THREE.Vector3(1, 1, 1);
  private sc = new THREE.Vector3();
  private q = new THREE.Quaternion();
  private q2 = new THREE.Quaternion();
  private e = new THREE.Euler();
  private col = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    r: Field,
  ) {
    this.group.name = 'dragon-field';
    this.n = r.harpoons.length;
    this.fixed = r.fixed;
    this.broken = r.fixed;
    this.fired = r.harpoons.map(() => -1);
    this.stuck = r.harpoons.map(() => false);
    this.taut = r.harpoons.map(() => -9);
    this.loaded = r.harpoons.map(() => true);
    this.hold = r.harpoons.map(() => 0);
    this.lit = r.harpoons.map(() => 0);
    this.pulled = r.harpoons.map(() => -9);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 7);
    const id = new THREE.Matrix4();

    // The engines: the first Vigil's, the old trap's carriage and bow, aimed at the middle; the levers' posts.
    const body = new Kit();
    const beam = (i: number): string => BEAM[((i % BEAM.length) + BEAM.length) % BEAM.length] as string;
    const ARC = Math.PI * 0.62;
    const prod = new THREE.TorusGeometry(1.0, 0.07, 5, 18, ARC).rotateZ(-Math.PI / 2 - ARC / 2).rotateX(-Math.PI / 2);
    const lump = new THREE.IcosahedronGeometry(0.5, 0);
    const rope = (a: THREE.Vector3, b: THREE.Vector3, sag: number): void => {
      const k = 8;
      for (let i = 0; i < k; i++) {
        const p0 = a.clone().lerp(b, i / k);
        const p1 = a.clone().lerp(b, (i + 1) / k);
        p0.y -= Math.sin((i / k) * Math.PI) * sag;
        p1.y -= Math.sin(((i + 1) / k) * Math.PI) * sag;
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), p1.clone().sub(p0).normalize());
        body.add(cyl, ROPE, new THREE.Matrix4().compose(p0.clone().add(p1).multiplyScalar(0.5), q, new THREE.Vector3(0.035, p0.distanceTo(p1), 0.035)));
      }
    };
    for (const h of r.harpoons) {
      const at = ctx.at(h.x, h.y, 0);
      const yaw = Math.atan2(-at.x, -at.z);
      const g = ctx.ground(at.x, at.z);
      const m = place(at.x, g - 0.1, at.z, yaw, 1.15, -0.1, 0.04);
      for (const s of [-1, 1]) {
        body.add(box, beam(s + 2), m, place(s * 0.34, 0.5, 0, 0, new THREE.Vector3(0.14, 0.18, 2.6)));
        body.add(box, beam(s + 3), m, place(s * 0.36, 0.2, 0.95, 0, new THREE.Vector3(0.12, 0.62, 0.12), 0.1, s * 0.12));
        body.add(box, beam(s + 1), m, place(s * 0.36, 0.1, -0.9, 0, new THREE.Vector3(0.12, 0.44, 0.12), -0.2, s * 0.15));
      }
      for (const z of [-1.0, -0.3, 0.4, 1.05]) body.add(box, beam(Math.round(z * 3)), m, place(0, 0.48, z, 0, new THREE.Vector3(0.82, 0.1, 0.16)));
      body.add(box, '#141011', m, place(0, 0.61, 0.05, 0, new THREE.Vector3(0.2, 0.06, 2.5)));
      body.add(prod, IRON, m, place(0, 0.74, 0.3));
      const tipX = Math.sin(ARC / 2);
      const tipZ = 0.3 + Math.cos(ARC / 2);
      for (const s of [-1, 1]) {
        body.add(new THREE.ConeGeometry(0.08, 0.3, 5), IRON_DARK, m, place(s * tipX, 0.74, tipZ - 0.05, 0, 1, -Math.PI / 2 + 0.5, s * 0.5));
        const a = new THREE.Vector3(s * tipX, 0.74, tipZ - 0.15);
        const b = new THREE.Vector3(0, 0.74, -0.55);
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
        body.add(cyl, '#8a7a68', m, new THREE.Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), q, new THREE.Vector3(0.025, a.distanceTo(b), 0.025)));
      }
      body.add(box, IRON, m, place(0, 0.7, -0.55, 0, new THREE.Vector3(0.3, 0.12, 0.16)));
      body.add(box, IRON_DARK, m, place(0, 0.7, 1.28, 0, new THREE.Vector3(0.6, 0.3, 0.12)));
      for (const x of [-0.22, 0, 0.22]) body.add(new THREE.SphereGeometry(0.035, 5, 4), IRON, m, place(x, 0.78, 1.35));
      // A drum of chain at the back, the chain run up to the harpoon's eye.
      body.add(cyl, IRON_DARK, m, place(0, 0.62, -1.15, 0, new THREE.Vector3(0.34, 0.8, 0.34), 0, Math.PI / 2));
      for (let i = 0; i < 5; i++) body.add(new THREE.TorusGeometry(0.17, 0.035, 4, 8), IRON, m, place(-0.3 + i * 0.15, 0.62, -1.15, Math.PI / 2));
      for (const [x, z, s] of [[-0.5, -0.9, 0.5], [0.45, -0.7, 0.42], [0.2, -1.25, 0.6], [-0.4, 0.2, 0.3]] as const) body.add(lump, '#0e0b0d', m, place(x, 0.0, z, x * 3, new THREE.Vector3(s, s * 0.45, s)));
      const lever = ctx.at(h.lx, h.ly, 0);
      const lg = ctx.ground(lever.x, lever.z);
      const ly = Math.atan2(at.x - lever.x, at.z - lever.z);
      const lm = place(lever.x, lg, lever.z, ly);
      body.add(box, beam(1), lm, place(0, 0.35, 0, 0, new THREE.Vector3(0.16, 0.7, 0.16)));
      body.add(box, beam(2), lm, place(0, 0.05, 0, 0, new THREE.Vector3(0.5, 0.1, 0.5)));
      // A stone ring round the lever's foot: where to stand.
      body.add(new THREE.TorusGeometry(0.55, 0.06, 4, 16).rotateX(Math.PI / 2), '#5a5458', lm, place(0, 0.04, 0));
      rope(new THREE.Vector3(lever.x, lg + 0.5, lever.z), new THREE.Vector3(at.x, g + 0.45, at.z), 0.3);
      const dir = new THREE.Vector3(-at.x, 0, -at.z).normalize();
      const rest = m.clone().multiply(place(0, 0.72, 0.15));
      this.guns.push({ at, mouth: at.clone().addScaledVector(dir, 1.5).setY(g + 0.7), lever: lever.setY(lg), yaw, rest });
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.12, flatShading: true });
    this.mats.push(mat);
    const engines = body.build(mat, 'dragon-harpoons');
    if (engines) {
      engines.castShadow = !ctx.low;
      engines.receiveShadow = true;
      this.geos.push(engines.geometry);
      this.group.add(engines);
    }

    // The harpoons: a long black shaft, a barbed iron head, fins of hide, an eye for the chain.
    const hk = new Kit();
    hk.add(cyl, '#6a5848', id, place(0, 0, 0, 0, new THREE.Vector3(0.1, 2.4, 0.1), Math.PI / 2));
    for (const z of [-0.6, 0.2]) hk.add(new THREE.CylinderGeometry(0.065, 0.065, 0.07, 7), IRON, id, place(0, 0, z, 0, 1, Math.PI / 2));
    hk.add(new THREE.ConeGeometry(0.17, 0.5, 4), '#a8a4a0', id, place(0, 0, 1.42, 0, 1, Math.PI / 2));
    for (const s of [-1, 1]) hk.add(new THREE.ConeGeometry(0.06, 0.3, 3), '#8a8682', id, place(s * 0.12, 0, 1.15, 0, 1, -Math.PI / 2 + 0.3, s * 0.5));
    for (let i = 0; i < 3; i++) hk.add(new THREE.BoxGeometry(0.012, 0.26, 0.36), '#8a2e26', id, place(Math.sin((i / 3) * Math.PI * 2) * 0.1, Math.cos((i / 3) * Math.PI * 2) * 0.1, -1.05, 0, 1, 0, -(i / 3) * Math.PI * 2));
    hk.add(new THREE.TorusGeometry(0.08, 0.025, 4, 8), IRON, id, place(0, 0, -1.25));
    const hmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3, flatShading: true });
    this.mats.push(hmat);
    this.harpoons = this.instanced(hk, hmat, this.n, 'dragon-harpoon');

    // The levers' arms.
    const ak = new Kit();
    ak.add(box, '#3a2e26', id, place(0, 0.3, 0, 0, new THREE.Vector3(0.08, 0.7, 0.08)));
    ak.add(new THREE.SphereGeometry(0.08, 6, 4), IRON, id, place(0, 0.66, 0));
    const amat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.mats.push(amat);
    this.levers = this.instanced(ak, amat, this.n, 'dragon-lever');

    // The chains: links of black iron, from the engine's mouth up into the dragon.
    const ck = new Kit();
    ck.add(new THREE.TorusGeometry(0.5, 0.18, 4, 8).scale(1, 1.6, 1), '#57524e', id);
    const cmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.45, flatShading: true });
    this.mats.push(cmat);
    this.chains = this.instanced(ck, cmat, this.n * LINKS, 'dragon-chain');

    // The plates: a shard of black glass, flat against the body, its facets catching the light.
    const pk = new Kit();
    pk.add(new THREE.IcosahedronGeometry(0.34, 0), '#ffffff', id, place(0, 0, 0, 0, new THREE.Vector3(1.3, 1.05, 0.3)));
    pk.add(new THREE.OctahedronGeometry(0.16, 0), '#ffffff', id, place(0, 0.06, 0.08, 0, new THREE.Vector3(1, 1.2, 0.5)));
    // Lit facets, and a light of their own the colour of their state (the instance colour drives the emissive too):
    // compiled once with the board, never changed after.
    const pmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.12, metalness: 0.45, flatShading: true, emissive: '#ffffff', emissiveIntensity: 1.1 });
    pmat.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace('vec3 totalEmissiveRadiance = emissive;', 'vec3 totalEmissiveRadiance = emissive * vColor.rgb;');
    };
    pmat.customProgramCacheKey = () => 'dragon-plates';
    this.mats.push(pmat);
    this.plates = this.instanced(pk, pmat, 6, 'dragon-plates');
    this.plates.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(18), 3);
    for (let i = 0; i < 6; i++) this.plates.setColorAt(i, WHOLE);

    // The lanes: a ribbon on the ground from each engine in toward the middle, past the dragon's circle.
    const k = WORLD_PER_HEX / 1000;
    const len = (DRAGON.harpoons.ring - DRAGON.orbit + 1600) * k;
    const half = (DRAGON.harpoons.lane / 2) * k;
    const pos: number[] = [];
    const uv: number[] = [];
    const lane: number[] = [];
    const idx: number[] = [];
    const steps = 24;
    this.guns.forEach((g0, li) => {
      const dir = new THREE.Vector3(-g0.at.x, 0, -g0.at.z).normalize();
      const side = new THREE.Vector3(dir.z, 0, -dir.x);
      const base = pos.length / 3;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        for (const sx of [-1, 1]) {
          const p = g0.at.clone().addScaledVector(dir, 1.2 + (len - 1.2) * t).addScaledVector(side, sx * half);
          pos.push(p.x, ctx.ground(p.x, p.z) + 0.05, p.z);
          uv.push(sx < 0 ? 0 : 1, t);
          lane.push(li);
        }
        if (i < steps) idx.push(base + i * 2, base + i * 2 + 1, base + i * 2 + 2, base + i * 2 + 1, base + i * 2 + 3, base + i * 2 + 2);
      }
    });
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    lg.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    lg.setAttribute('aLane', new THREE.Float32BufferAttribute(lane, 1));
    lg.setIndex(idx);
    this.lanes = laneMat();
    this.mats.push(this.lanes);
    this.geos.push(lg);
    const lm = new THREE.Mesh(lg, this.lanes);
    lm.renderOrder = 2;
    lm.frustumCulled = false;
    this.group.add(lm);

    // The fury's glass: one plane over the whole clearing (shown only while it rages, once compiled).
    const gg = new THREE.CircleGeometry((r.radius + 2.5) * WORLD_PER_HEX, 48).rotateX(-Math.PI / 2);
    this.glass = glassMat();
    this.mats.push(this.glass);
    this.geos.push(gg);
    this.glassMesh = new THREE.Mesh(gg, this.glass);
    this.glassMesh.position.y = ctx.ground(0, 0) + 0.07;
    this.glassMesh.renderOrder = 1;
    this.glassMesh.frustumCulled = false;
    this.group.add(this.glassMesh);
    this.group.add(this.glow.mesh);
    // Its first frames draw everything (the harpoons in their grooves, the chains and plates hidden under the
    // board), so the shaders compile with the rest of the board's.
    this.placeAll();
  }

  private instanced(k: Kit, mat: THREE.Material, count: number, name: string): THREE.InstancedMesh {
    const one = k.build(mat, name) as THREE.Mesh;
    this.geos.push(one.geometry);
    const im = new THREE.InstancedMesh(one.geometry, mat, count);
    im.name = name;
    im.frustumCulled = false;
    im.castShadow = !this.ctx.low;
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.group.add(im);
    return im;
  }

  /** The dragon this frame, as the view drew it (null: not out). */
  setBody(p: THREE.Vector3 | null, q?: THREE.Quaternion, scale = 1, flying = false, anim?: AnimParams): void {
    if (!p || !q) {
      this.body = null;
      return;
    }
    // Reused every frame (no garbage): the same two objects, copied into.
    const b = this.bodyBuf;
    this.body = b;
    b.p.copy(p);
    b.q.copy(q);
    b.scale = scale;
    b.flying = flying;
    b.anim = anim;
  }

  /** Where a plate is (world), from the dragon as drawn. */
  private plateAt(i: number, out: THREE.Vector3): THREE.Vector3 {
    const b = this.body;
    const pl = PLATES[i] as [number, number, number, number];
    out.set(pl[0], pl[1] - (b?.flying ? 0.1 : 0), pl[2]);
    if (!b) return out.set(0, -50, 0);
    if (b.anim) posePoint(b.anim, 2, out);
    return out.multiplyScalar(b.scale).applyQuaternion(b.q).add(b.p);
  }

  /** Every fresh sim tick: the harpoons fired, the scales broken and closed, the fall, the fury. */
  tick(s: BattleState): void {
    const st = s.dragon;
    const low = this.ctx.low;
    if (st) {
      const down = (s._events ?? []).some((e) => e.t === 'boom' && e.kind === 'dragonDown');
      for (let i = 0; i < this.n; i++) {
        // The two that brought it down stay taut a moment while it falls.
        if (down && this.stuck[i]) this.taut[i] = this.now;
        this.stuck[i] = (st.stuck[i] ?? 0) > s.tick;
        this.loaded[i] = (st.ready[i] ?? 0) <= s.tick;
        this.hold[i] = Math.min(1, (st.lever[i] ?? 0) / DRAGON.harpoons.lever);
      }
      this.broken = st.broken;
      this.cold = s.rules?.dragon?.ask ? Math.min(1, st.cold / s.rules.dragon.ask) : 0;
      this.brittle = st.phase === 'down' || this.cold >= 1;
      this.fury = st.phase === 'fury';
      const u = this.glass.uniforms as { uGlass: THREE.IUniform<number>; uWarn: THREE.IUniform<number> };
      u.uGlass.value = this.fury ? st.glass : 99;
      this.glassR = this.fury ? st.glass : -1;
      const warn = DRAGON.fury.warn * ((s.rules?.dragon?.warnBp ?? 10000) / 10000);
      const left = st.glassAt - s.tick;
      u.uWarn.value = this.fury && left <= warn ? 1 - left / Math.max(1, warn) : 0;
      this.glassMesh.visible = this.fury || s.tick < 30;
    }
    for (const e of s._events ?? []) {
      if (e.t === 'shot' && e.kind === 'harpoon') {
        const i = this.guns.findIndex((g) => Math.abs(g.at.x - this.ctx.at(e.x, e.y).x) < 0.3 && Math.abs(g.at.z - this.ctx.at(e.x, e.y).z) < 0.3);
        if (i < 0) continue;
        const g = this.guns[i] as (typeof this.guns)[number];
        this.fired[i] = this.now;
        this.pulled[i] = this.now;
        this.ctx.shake(0.22);
        this.ctx.flare(g.mouth, '#ffe2c8', 3.4, 10);
        this.ctx.vfx.burst(g.mouth, { n: low ? 6 : 14, color: '#7a7270', speed: 0.05, up: 0.02, size: 0.32, life: 22, smoke: true, spread: 0.4 });
        this.ctx.vfx.burst(g.mouth, { n: low ? 4 : 10, color: '#ff8a6e', speed: 0.07, up: 0.04, size: 0.05, life: 16, gravity: 0.004, spread: 0.3 });
        continue;
      }
      if (e.t !== 'boom') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      if (e.kind === 'dragonScale') {
        // A plate bursts: glass flying off it, a white flash, a red wound where it was.
        const i = Math.min(5, Math.max(0, (s.dragon?.broken ?? 1) - 1));
        const at = this.plateAt(i, this.v.clone());
        this.ctx.flare(at, '#ffffff', 5.2, 10);
        this.ctx.flare(at, '#cfe2ff', 3.4, 16);
        this.ctx.flare(at, '#ff4f3a', 2.6, 24);
        this.ctx.vfx.burst(at, { n: low ? 18 : 48, color: SHARDS, speed: 0.17, up: 0.08, size: 0.1, life: 30, gravity: 0.01, spread: 0.7 });
        this.ctx.vfx.burst(at, { n: low ? 6 : 14, color: '#3a3848', speed: 0.12, up: 0.07, size: 0.18, life: 34, gravity: 0.013, spread: 0.6 });
        this.ctx.vfx.burst(at, { n: low ? 4 : 10, color: '#ff8a6e', speed: 0.08, up: 0.05, size: 0.05, life: 22, gravity: 0.004, spread: 0.4 });
        this.ctx.vfx.ring(at, 0.2, 2.2, '#cfe2ff', 12, 0.9);
        this.ctx.vfx.beam(at, at.clone().add(new THREE.Vector3(0, 2.6, 0)), '#e6f1ff', 4, 0.12);
        this.ctx.shake(0.28);
      } else if (e.kind === 'dragonClose') {
        // Fire closes one: the glass runs orange and knits back.
        const i = Math.min(5, Math.max(0, s.dragon?.broken ?? 0));
        this.closed[i] = this.now;
        const at = this.plateAt(i, this.v.clone());
        this.ctx.flare(at, '#ff8a3d', 2.8, 22);
        this.ctx.vfx.burst(at, { n: low ? 6 : 16, color: '#ff8a3d', speed: 0.03, up: 0.04, size: 0.1, life: 24, gravity: -0.002, spread: 0.4 });
        this.ctx.vfx.burst(at, { n: low ? 2 : 5, color: '#3a2824', speed: 0.015, up: 0.03, size: 0.5, life: 26, smoke: true, spread: 0.3 });
      } else if (e.kind === 'dragonDown') {
        // Brought down: the chains go taut, it falls (the view drops it), and a moment later it hits the glass.
        this.ctx.shake(0.35);
        this.ctx.flare(p.clone().setY(p.y + 2.2), '#f2f6ff', 4, 8);
        this.impact = { at: this.now + 0.38, p: p.clone() };
      } else if (e.kind === 'dragonRise') {
        this.ctx.shake(0.35);
        this.ctx.vfx.ring(p, 0.3, 4.2, '#6e6460', 18, 0.7);
        this.ctx.vfx.burst(p, { n: low ? 8 : 20, color: '#3a3330', speed: 0.07, up: 0.06, size: 0.6, life: 28, smoke: true, spread: 1.2 });
      } else if (e.kind === 'dragonFury') {
        this.ctx.shake(0.5);
        this.ctx.flare(p.setY(p.y + 2), '#ff4f3a', 6, 26);
      } else if (e.kind === 'dragonGlass') {
        // The rim comes in: embers and smoke all along the new ring.
        const R = (e.r / 1000 + 0.5) * WORLD_PER_HEX;
        const k = low ? 10 : 24;
        for (let j = 0; j < k; j++) {
          const a = (j / k) * Math.PI * 2 + this.now;
          const q = new THREE.Vector3(Math.cos(a) * R, 0, Math.sin(a) * R);
          q.y = this.ctx.ground(q.x, q.z) + 0.15;
          this.ctx.vfx.burst(q, { n: 1, color: j % 3 ? '#ff8a3d' : '#ff4f3a', speed: 0.02, up: 0.05, size: 0.09, life: 22, gravity: -0.002, spread: 0.2 });
          if (j % 3 === 0) this.ctx.vfx.burst(q, { n: 1, color: '#2a1e1c', speed: 0.01, up: 0.03, size: 0.7, life: 26, smoke: true, spread: 0.2 });
        }
        this.ctx.shake(0.12);
      }
    }
  }

  /** It hits the ground: glass dust and shards out in a ring, the board jolts hard. */
  private land(p: THREE.Vector3): void {
    const low = this.ctx.low;
    this.ctx.shake(0.95);
    this.ctx.flare(p, '#ffe2c8', 6.5, 16);
    // A shockwave across the glass: three rings out from where it hit, the widest reaching half the clearing.
    this.ctx.vfx.ring(p, 0.6, 9.5, '#b8aca6', 30, 0.95);
    this.ctx.vfx.ring(p, 0.4, 6.4, '#e6f1ff', 22, 0.9);
    this.ctx.vfx.ring(p, 0.2, 3.6, '#ff4f3a', 14, 0.85);
    this.ctx.vfx.burst(p, { n: low ? 20 : 56, color: '#8a8290', speed: 0.13, up: 0.06, size: 0.9, life: 46, smoke: true, spread: 2.6 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 24 : 70, color: SHARDS, speed: 0.2, up: 0.13, size: 0.12, life: 40, gravity: 0.011, spread: 2.0 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: '#ff8a3d', speed: 0.12, up: 0.08, size: 0.06, life: 26, gravity: 0.008, spread: 1.2 });
  }

  /** Sparks off the fury's front: a few each frame along the ring where the glass is coming in. */
  private frontSparks(): void {
    const R = this.glassR;
    if (R < 0 || R > 40) return;
    const k = this.ctx.low ? 1 : 3;
    for (let j = 0; j < k; j++) {
      const side = Math.floor(Math.random() * 6);
      const t = Math.random();
      const [a, b] = [HEX_DIRS[side] as [number, number], HEX_DIRS[(side + 1) % 6] as [number, number]];
      const qx = (a[0] + (b[0] - a[0]) * t) * (R + 0.5);
      const rx = (a[1] + (b[1] - a[1]) * t) * (R + 0.5);
      const at = this.ctx.at(qx * 1000 + rx * 500, rx * 866, 0.1);
      this.ctx.vfx.burst(at, { n: 1, color: Math.random() < 0.3 ? '#ffd2a0' : '#ff6a3d', speed: 0.015, up: 0.05, size: 0.07, life: 18, gravity: -0.002, spread: 0.25 });
    }
  }

  /** Every redraw: the harpoons, the levers, the chains, the plates, the lanes, the glints. */
  draw(s: BattleState): void {
    if (this.impact && this.now >= this.impact.at) {
      const at = this.body ? this.body.p.clone().setY(this.ctx.ground(this.body.p.x, this.body.p.z) + 0.1) : this.impact.p;
      this.impact = null;
      this.land(at);
    }
    if (this.fury && Math.random() < 0.7) this.frontSparks();
    for (let i = 0; i < this.n; i++) {
      const lit = laneLit(s, i) ? 1 : 0;
      this.lit[i] = (this.lit[i] ?? 0) + (lit - (this.lit[i] ?? 0)) * 0.18;
    }
    const u = this.lanes.uniforms as { uT: THREE.IUniform<number>; uOn: THREE.IUniform<THREE.Vector4> };
    u.uT.value = this.now;
    u.uOn.value.set(this.lit[0] ?? 0, this.lit[1] ?? 0, this.lit[2] ?? 0, this.lit[3] ?? 0);
    (this.glass.uniforms.uT as THREE.IUniform<number>).value = this.now;
    this.placeAll();
    this.glow.begin();
    // The levers: a glint while lit, fuller as it is held; the harpoon's head while loaded.
    for (let i = 0; i < this.n; i++) {
      const g = this.guns[i] as (typeof this.guns)[number];
      const l = this.lit[i] ?? 0;
      if (l > 0.05) this.glow.add(g.lever.x, g.lever.y + 0.9, g.lever.z, EMBER, 0.9 + 1.4 * (this.hold[i] ?? 0), (0.25 + 0.2 * Math.sin(this.now * 8)) * l + 0.45 * (this.hold[i] ?? 0));
      if (l > 0.05) this.glow.add(g.lever.x, g.lever.y + 0.08, g.lever.z, EMBER, 1.6, 0.18 * l);
      if (this.loaded[i] && !this.stuck[i] && (this.fired[i] ?? -1) < 0) this.glow.add(g.mouth.x, g.mouth.y, g.mouth.z, SOFT, 0.7, 0.12 + 0.18 * l);
    }
    // The plates' light: frost as the cold fills, white flicker when brittle, a red wound where one broke.
    if (this.body) {
      // Bigger in the sky, where the camera sees it small and dark.
      const big = this.body.flying ? 1.6 : 1.1;
      for (let i = 0; i < 6; i++) {
        const at = this.plateAt(i, this.v);
        const sh = this.shown[i] ?? 0;
        const sinceClose = this.now - (this.closed[i] ?? -9);
        if (i < this.broken) {
          const fixed = i < this.fixed;
          this.glow.add(at.x, at.y, at.z, fixed ? SOFT : EMBER, 1.0 * big * this.body.scale, (fixed ? 0.4 : 0.6) + 0.18 * Math.sin(this.now * 3 + i));
        } else if (sinceClose < 1.2) this.glow.add(at.x, at.y, at.z, FIRE, 1.4 * big * this.body.scale, 0.8 * (1 - sinceClose / 1.2));
        else if (sh > 0.5) {
          // Whole: a cold glint off the glass, so the six read on the model; brighter as the cold fills, white and
          // flickering once brittle.
          const flick = this.brittle ? 0.5 + 0.5 * Math.sin(this.now * 18 + i * 1.7) : 0;
          const glint = 0.5 + 0.5 * Math.sin(this.now * 1.7 + i * 2.3);
          this.glow.add(at.x, at.y, at.z, this.brittle ? WHITE : ICE, (0.6 + 0.6 * this.cold + 0.4 * flick) * big * this.body.scale, 0.22 + 0.1 * glint + 0.3 * this.cold + 0.35 * flick);
        }
      }
    }
    this.glow.end();
  }

  private placeAll(): void {
    const hidden = this.hidden;
    const UP = Y_AXIS;
    // The harpoons: in the groove while loaded; in flight toward the dragon; hidden once in it (the chain shows).
    for (let i = 0; i < this.n; i++) {
      const g = this.guns[i] as (typeof this.guns)[number];
      const since = (this.fired[i] ?? -1) >= 0 ? this.now - (this.fired[i] as number) : -1;
      if (since >= 0 && since < FLIGHT && this.body) {
        this.to.copy(this.body.p).setY(this.body.p.y + 1.6 * this.body.scale);
        this.a.copy(g.mouth).lerp(this.to, since / FLIGHT);
        this.q.setFromUnitVectors(Z_AXIS, this.b.copy(this.to).sub(g.mouth).normalize());
        this.harpoons.setMatrixAt(i, this.m.compose(this.a, this.q, this.one));
      } else {
        if (since >= FLIGHT) this.fired[i] = -1;
        this.harpoons.setMatrixAt(i, this.loaded[i] && !this.stuck[i] ? g.rest : hidden);
      }
      // The lever: thrown down a moment when it fires, leaning as it is held.
      const ps = this.now - (this.pulled[i] ?? -9);
      const tilt = ps < 0.4 ? -1.0 * Math.sin(Math.min(1, ps / 0.1) * Math.PI * 0.5) : ps < 1.2 ? -1.0 * (1 - (ps - 0.4) / 0.8) : -0.25 * (this.hold[i] ?? 0);
      this.q.setFromEuler(this.e.set(0.35 + tilt, Math.atan2(g.at.x - g.lever.x, g.at.z - g.lever.z), 0, 'YXZ'));
      this.levers.setMatrixAt(i, this.m.compose(this.a.copy(g.lever).setY(g.lever.y + 0.62), this.q, this.one));
      // The chain: from the mouth up into it, sagging, trembling.
      const taut = this.now - (this.taut[i] ?? -9) < 0.75;
      const on = (this.stuck[i] || taut) && this.body;
      if (on && this.body) this.to.copy(this.body.p).setY(this.body.p.y + 1.5 * this.body.scale);
      // Taut as it is pulled down: no sag, and a hard tremble.
      const sag = !on ? 0 : taut ? 0 : 0.6 + 0.08 * g.mouth.distanceTo(this.to);
      for (let j = 0; j < LINKS; j++) {
        const k = i * LINKS + j;
        if (!on) {
          this.chains.setMatrixAt(k, hidden);
          continue;
        }
        const t0 = j / LINKS;
        const t1 = (j + 1) / LINKS;
        this.a.copy(g.mouth).lerp(this.to, t0);
        this.a.y -= Math.sin(t0 * Math.PI) * sag;
        this.b.copy(g.mouth).lerp(this.to, t1);
        this.b.y -= Math.sin(t1 * Math.PI) * sag;
        const len = this.a.distanceTo(this.b);
        this.q.setFromUnitVectors(UP, this.v.copy(this.b).sub(this.a).normalize());
        // Every other link turned a quarter, as chain is.
        if (j % 2) this.q.multiply(this.q2.setFromAxisAngle(UP, Math.PI / 2));
        this.a.add(this.b).multiplyScalar(0.5);
        this.a.y += (taut ? 0.09 : 0.03) * Math.sin(this.now * (taut ? 47 : 23) + j);
        this.chains.setMatrixAt(k, this.m.compose(this.a, this.q, this.sc.set(0.22, len * 0.62, 0.22)));
      }
    }
    // The plates: on the dragon, shown as whole (eased), frosted, white when brittle, orange as fire knits one.
    for (let i = 0; i < 6; i++) {
      const whole = i >= this.broken ? 1 : 0;
      this.shown[i] = (this.shown[i] ?? 1) + (whole - (this.shown[i] ?? 1)) * (whole ? 0.08 : 0.5);
      const sh = this.shown[i] ?? 0;
      if (!this.body || sh < 0.03) {
        this.plates.setMatrixAt(i, this.body ? hidden : this.m.makeTranslation(0, -50, 0));
        continue;
      }
      const at = this.plateAt(i, this.a);
      const pl = PLATES[i] as [number, number, number, number];
      this.q.copy(this.body.q).multiply(this.q2.setFromEuler(this.e.set(pl[1] > 2.4 ? -Math.PI / 2 : 0, pl[3], 0)));
      this.plates.setMatrixAt(i, this.m.compose(at, this.q, this.sc.setScalar(this.body.scale * sh * (this.body.flying ? 1.25 : 1.05))));
      const sinceClose = this.now - (this.closed[i] ?? -9);
      this.col.copy(WHOLE).lerp(FROST, this.cold * 0.75);
      if (this.brittle) this.col.lerp(WHITE, 0.55 + 0.4 * Math.sin(this.now * 14 + i));
      if (sinceClose < 1.4) this.col.lerp(FIRE, 1 - sinceClose / 1.4);
      this.plates.setColorAt(i, this.col);
    }
    this.harpoons.instanceMatrix.needsUpdate = true;
    this.levers.instanceMatrix.needsUpdate = true;
    this.chains.instanceMatrix.needsUpdate = true;
    this.plates.instanceMatrix.needsUpdate = true;
    if (this.plates.instanceColor) this.plates.instanceColor.needsUpdate = true;
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.harpoons.dispose();
    this.levers.dispose();
    this.chains.dispose();
    this.plates.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
